import { useState, useCallback } from 'react'
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import { Member, User, UserRole } from '@/types'
import toast from 'react-hot-toast'

export interface EnrichedMember extends Member {
    id: string
    user: User | null
}

export function useMembers() {
    const { currentOrg } = useAuthStore()
    const [members, setMembers] = useState<EnrichedMember[]>([])
    const [loading, setLoading] = useState(false)

    const fetchMembers = useCallback(async () => {
        if (!currentOrg) return

        setLoading(true)
        try {
            // 1. Buscar membros da organização
            const membersRef = collection(db, 'members')
            const q = query(membersRef, where('orgId', '==', currentOrg.id))
            const snapshot = await getDocs(q)

            const rawMembers = snapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...(doc.data() as Omit<Member, 'id'>)
                }))
                .filter((m: any) => m.userId) as Member[]

            // 2. Enriquecer com dados do usuário (Parallel Fetch)
            const enriched: EnrichedMember[] = await Promise.all(
                rawMembers.map(async (member) => {
                    // Assumindo que member.userId é o ID do usuário na coleção users
                    const userRef = doc(db, 'users', member.userId)
                    const userSnap = await getDoc(userRef)
                    const userData = userSnap.exists() ? (userSnap.data() as User) : null

                    return {
                        ...member,
                        id: (member as any).id, // Firestore Doc ID da coleção members
                        user: userData
                    }
                })
            )

            setMembers(enriched)
        } catch (error) {
            console.error('Erro ao buscar membros:', error)
            toast.error('Erro ao carregar lista de membros')
        } finally {
            setLoading(false)
        }
    }, [currentOrg])

    const updateMemberRole = async (memberId: string, newRole: UserRole) => {
        try {
            await updateDoc(doc(db, 'members', memberId), { role: newRole })
            setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m))
            toast.success('Permissão atualizada com sucesso')
        } catch (error) {
            console.error('Erro ao atualizar permissão:', error)
            toast.error('Erro ao atualizar permissão')
            throw error
        }
    }

    const removeMember = async (memberId: string) => {
        try {
            await deleteDoc(doc(db, 'members', memberId))
            setMembers(prev => prev.filter(m => m.id !== memberId))
            toast.success('Membro removido com sucesso')
        } catch (error) {
            console.error('Erro ao remover membro:', error)
            toast.error('Erro ao remover membro')
            throw error
        }
    }

    return {
        members,
        loading,
        fetchMembers,
        updateMemberRole,
        removeMember
    }
}
